import React, { useState, useEffect, useRef, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/PodUpload.css";


/* ============================================================
   CONFIG — override via props
   ============================================================ */
const DEFAULT_SLOTS = [
  { key: "photo1", label: "Photo 1", required: true },
  { key: "photo2", label: "Photo 2", required: false },
];
const MAX_DIM = 1600;
const JPEG_QUALITY = 0.8;
const MAX_BYTES = 5 * 1024 * 1024;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const fmtSize = (b) =>
  b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.round(b / 1024) + " KB";

/* Compress on device to save mobile data */
function compressImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      c.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(
            blob && blob.size < file.size
              ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" })
              : file
          );
        },
        "image/jpeg",
        JPEG_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

/* ============================================================
   Icons
   ============================================================ */
const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="20" y1="12" x2="4" y2="12" />
    <polyline points="10 6 4 12 10 18" />
  </svg>
);
const PlaceholderIcon = () => (
  <svg className="pod-ph" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14v14zm-5.04-6.71-2.75 3.54-1.96-2.36L6.5 17h11l-3.54-4.71zM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
  </svg>
);
const CameraIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 5h-3.2L15 3H9L7.2 5H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-8 13a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
  </svg>
);
const GalleryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);
const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);
const FlipIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7h-3l-2-3H9L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
    <polyline points="15 11 16.5 12.5 15 14" />
    <path d="M8 13.5a4 4 0 0 1 8.5-1" />
    <polyline points="9 15 7.5 13.5 9 12" />
    <path d="M16 11.5a4 4 0 0 1-8.5 1" />
  </svg>
);

/* ============================================================
   Toast hook
   ============================================================ */
function useToast() {
  const [toast, setToast] = useState({ msg: "", type: "", show: false });
  const timer = useRef();
  const show = useCallback((msg, type = "") => {
    clearTimeout(timer.current);
    setToast({ msg, type, show: true });
    timer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, show];
}

/* ============================================================
   Camera modal
   ============================================================ */
function CameraModal({ title, onClose, onCapture, onFallback, notify }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [captured, setCaptured] = useState(null); // { blob, url }
  const [flash, setFlash] = useState(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startStream = useCallback(
    async (mode) => {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    },
    [stopStream]
  );

  // Start / restart camera when facing changes or after retake
  useEffect(() => {
    if (captured) return;
    let cancelled = false;
    startStream(facing).catch((err) => {
      if (cancelled) return;
      if (err.name === "NotAllowedError") {
        notify("Camera permission denied. Allow camera access in settings.", "bad");
        onClose();
      } else {
        onFallback(); // open native camera instead
      }
    });
    return () => {
      cancelled = true;
    };
  }, [facing, captured, startStream, notify, onClose, onFallback]);

  // Cleanup on unmount, lock body scroll, Esc to close
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    const onVis = () => {
      if (document.hidden) stopStream();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onVis);
      stopStream();
    };
  }, [onClose, stopStream]);

  useEffect(() => () => captured && URL.revokeObjectURL(captured.url), [captured]);

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) {
      notify("Camera not ready yet", "bad");
      return;
    }
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setFlash(true);
    setTimeout(() => setFlash(false), 60);
    navigator.vibrate?.(30);
    c.toBlob(
      (blob) => {
        stopStream();
        setCaptured({ blob, url: URL.createObjectURL(blob) });
      },
      "image/jpeg",
      0.92
    );
  };

  const usePhoto = () => {
    if (!captured) return;
    onCapture(new File([captured.blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" }));
  };

  return (
    <div className="pod-cam" role="dialog" aria-modal="true" aria-label="Camera">
      <video ref={videoRef} autoPlay playsInline muted className={captured ? "pod-hidden" : ""} />
      {captured && <img className="pod-still" src={captured.url} alt="Captured" />}
      <div className={`pod-flash ${flash ? "on" : ""}`} />

      <div className="pod-cam-top">
        <button className="pod-cam-btn" type="button" onClick={onClose} aria-label="Close camera">
          <CloseIcon />
        </button>
        <span>{title}</span>
        <span style={{ width: 52 }} />
      </div>

      {!captured ? (
        <div className="pod-cam-bar">
          <span style={{ width: 52 }} />
          <button className="pod-shutter" type="button" onClick={shoot} aria-label="Take photo" />
          <button
            className="pod-cam-btn"
            type="button"
            aria-label="Switch camera"
            onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
          >
            <FlipIcon />
          </button>
        </div>
      ) : (
        <div className="pod-cam-bar">
          <button className="pod-cam-txt" type="button" onClick={() => setCaptured(null)}>
            Retake
          </button>
          <button className="pod-cam-txt use" type="button" onClick={usePhoto}>
            Use Photo
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Photo slot
   ============================================================ */
function PhotoSlot({ slot, photo, invalid, removing, onCamera, onPick, onRemove }) {
  const nativeCamRef = useRef(null);
  const galleryRef = useRef(null);

  const handleInput = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onPick(slot, f);
  };

  // Parent can trigger native camera fallback via this id
  return (
    <div className="pod-photo">
      <div
        className={`pod-thumb ${invalid ? "invalid" : ""}`}
        role="button"
        tabIndex={0}
        aria-label={slot.label}
        onClick={() => onCamera(slot, nativeCamRef)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onCamera(slot, nativeCamRef)}
      >
        {photo ? (
          <>
            <img src={photo.url} alt={slot.label} />
            <button
              type="button"
              className="pod-remove"
              aria-label="Remove"
              disabled={removing}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(slot);
              }}
            >
              {removing ? <span className="pod-spinner" /> : "✕"}
            </button>
            {/* "Uploaded" = already saved from an earlier submit (existing: true, no
                local File); "Ready" = freshly picked this session, not sent yet. */}
            <span className="pod-done">{photo.existing ? "Uploaded" : "Ready"}</span>
          </>
        ) : (
          <PlaceholderIcon />
        )}
      </div>

      <div className="pod-actions">
        <div className="pod-slot-title">
          {slot.label} {slot.required && <span className="pod-req">*</span>}
        </div>
        <button type="button" className="pod-act" onClick={() => onCamera(slot, nativeCamRef)}>
          <CameraIcon />
          <span>Camera</span>
        </button>
        <button type="button" className="pod-act" onClick={() => galleryRef.current?.click()}>
          <GalleryIcon />
          <span>Gallery</span>
        </button>
        <div className="pod-meta">{photo?.file ? fmtSize(photo.file.size) : photo ? "Saved" : ""}</div>
        <input ref={nativeCamRef} type="file" accept="image/*" capture="environment" hidden onChange={handleInput} />
        <input ref={galleryRef} type="file" accept="image/*" hidden onChange={handleInput} />
      </div>
    </div>
  );
}

/* ============================================================
   Main screen
   ============================================================ */
export default function PodUpload({
  slots = DEFAULT_SLOTS,
  verifyUrl = (cn) => `/api/consignment/${encodeURIComponent(cn)}`,
  uploadUrl = "/api/consignment/pod-upload",
  demo = false, // API is wired — pass demo={true} to force mock data
  onBack = () => window.history.back(),
  onUploaded, // (consignmentNo, response) => void
}) {
  const [cnNo, setCnNo] = useState("");
  const [cnInvalid, setCnInvalid] = useState(false);
  const [verified, setVerified] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [photos, setPhotos] = useState({}); // key -> { file, url }
  const [invalidSlots, setInvalidSlots] = useState({});
  const [removingSlots, setRemovingSlots] = useState({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [camera, setCamera] = useState(null); // { slot, nativeRef }
  const [toast, notify] = useToast();

  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => () => Object.values(photosRef.current).forEach((p) => URL.revokeObjectURL(p.url)), []);

  /* ----- Verify ----- */
  const fetchConsignment = async (cn) => {
    if (demo) {
      await sleep(500);
      if (cn.length < 4) throw new Error("Consignment not found");
      return {
        cnNo: cn,
        consignor: "Shree Transformers Pvt. Ltd., Kalyan",
        consignee: "Gujarat Power Solutions, Vadodara",
        status: "Delivered",
      };
    }
    const res = await api.get(verifyUrl(cn));
    return res.data;
  };

  // A plain <img src="..."> never goes through the api instance, so if this app
  // authenticates with a bearer token (attached by an axios interceptor) rather than
  // a cookie, the browser wouldn't send it on that request and the image would 401.
  // Fetching it through api and converting to a local blob URL sidesteps that entirely
  // — same path already-picked photos use below.
  // Returns the raw blob too (not just its object URL): if the backend requires a
  // file on every POST, we can resubmit this same blob as the "file" for an untouched
  // required slot instead of forcing the user to reselect a photo they already uploaded.
  const loadExistingPhoto = async (url) => {
    const res = await api.get(url, { responseType: "blob" });
    return { blob: res.data, url: URL.createObjectURL(res.data) };
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const cn = cnNo.trim().toUpperCase();
    if (!cn) {
      setCnInvalid(true);
      notify("Enter consignment number", "bad");
      return;
    }
    setVerifying(true);
    // Clear whatever was showing for a previous consignment before this one loads,
    // so a slow request never leaves a stale photo on screen under the wrong cnNo.
    Object.values(photosRef.current).forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos({});
    setInvalidSlots({});
    try {
      const data = await fetchConsignment(cn);
      setVerified(data);

      // Photos already saved for this consignment (from an earlier upload) — load
      // and show them so a re-verify doesn't look like nothing was ever uploaded.
      // existing: true + file: null means "preview only, nothing freshly picked" —
      // the blob is kept alongside it so an untouched required slot can still be
      // resubmitted (see handleSubmit) without the user having to reselect it.
      const preloaded = {};
      let loadFailed = false;
      if (!demo) {
        if (data?.pod1Uploaded && data?.pod1Url) {
          try {
            const { blob, url } = await loadExistingPhoto(data.pod1Url);
            preloaded.photo1 = { file: null, blob, url, existing: true };
          } catch {
            loadFailed = true;
          }
        }
        if (data?.pod2Uploaded && data?.pod2Url) {
          try {
            const { blob, url } = await loadExistingPhoto(data.pod2Url);
            preloaded.photo2 = { file: null, blob, url, existing: true };
          } catch {
            loadFailed = true;
          }
        }
      }
      setPhotos(preloaded);
      if (loadFailed) notify("Some previously uploaded photos couldn't be loaded", "bad");
    } catch (err) {
      setVerified(null);
      notify(err.response?.data?.message || err.message, "bad");
    } finally {
      setVerifying(false);
    }
  };

  const handleCnChange = (e) => {
    const v = e.target.value.toUpperCase();
    setCnNo(v);
    setCnInvalid(false);
    if (verified && v.trim() !== verified.cnNo) {
      setVerified(null);
      Object.values(photosRef.current).forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos({});
      setInvalidSlots({});
    }
  };

  /* ----- Photos ----- */
  const setPhoto = useCallback(
    async (slot, file) => {
      if (!file.type.startsWith("image/")) {
        notify("Please select an image", "bad");
        return;
      }
      const small = await compressImage(file);
      if (small.size > MAX_BYTES) {
        notify("Image must be under 5 MB", "bad");
        return;
      }
      setPhotos((prev) => {
        if (prev[slot.key]) URL.revokeObjectURL(prev[slot.key].url);
        return { ...prev, [slot.key]: { file: small, url: URL.createObjectURL(small), existing: false } };
      });
      setInvalidSlots((p) => ({ ...p, [slot.key]: false }));
    },
    [notify]
  );

  const removePhoto = async (slot) => {
    const current = photosRef.current[slot.key];
    if (!current) return;

    if (current.existing) {
      // Actually saved on the server (not just a local pick) — clear it there too.
      // Without this call, pod_upload/pod_upload2 stays 'Y' forever: the "✕" only
      // ever cleared the preview, the backend never heard about it, and since photo2
      // is optional on every POST, a "removed-but-not-replaced" slot would just keep
      // showing the old photo on the next verify.
      setRemovingSlots((p) => ({ ...p, [slot.key]: true }));
      try {
        await api.delete(`/api/consignment/pod-image/${encodeURIComponent(verified.cnNo)}/${slot.key}`);
      } catch (err) {
        notify(err.response?.data?.message || err.message || "Couldn't remove photo", "bad");
        setRemovingSlots((p) => ({ ...p, [slot.key]: false }));
        return; // leave it showing — don't clear locally if the server call failed
      }
      setRemovingSlots((p) => ({ ...p, [slot.key]: false }));
    }

    setPhotos((prev) => {
      if (!prev[slot.key]) return prev;
      URL.revokeObjectURL(prev[slot.key].url);
      const next = { ...prev };
      delete next[slot.key];
      return next;
    });
  };

  const openCamera = (slot, nativeRef) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      nativeRef.current?.click(); // old browser / non-HTTPS
      return;
    }
    setCamera({ slot, nativeRef });
  };

  const closeCamera = useCallback(() => setCamera(null), []);
  const cameraFallback = useCallback(() => {
    const ref = camera?.nativeRef;
    setCamera(null);
    ref?.current?.click();
  }, [camera]);
  const cameraCapture = useCallback(
    (file) => {
      const slot = camera?.slot;
      setCamera(null);
      if (slot) setPhoto(slot, file);
    },
    [camera, setPhoto]
  );

  /* ----- Submit ----- */
  const uploadWithProgress = async (fd) => {
    const res = await api.post(uploadUrl, fd, {
      // Whatever the shared axios instance normally does to a request body (a default
      // Content-Type: application/json header, a global transformRequest that
      // JSON.stringifies everything, etc.) silently mangles FormData into "{}" — the
      // server then sees every [FromForm] field as empty, not just this one. These two
      // options force this one request back to a real multipart/form-data body with
      // the browser's own boundary, regardless of what the shared instance is doing.
      transformRequest: (data) => data,
      headers: { "Content-Type": undefined },
      onUploadProgress: (e) => e.total && setProgress((e.loaded / e.total) * 100),
    });
    return res.data;
  };

  const handleSubmit = async () => {
    if (!cnNo.trim()) {
      setCnInvalid(true);
      notify("Enter consignment number", "bad");
      return;
    }
    if (!verified) {
      notify("Verify the consignment first", "bad");
      return;
    }
    // "Required" means "must have a photo, either already uploaded or freshly picked
    // this session" — an existing preview (existing: true, no local file) DOES satisfy
    // it, so re-submitting without touching an already-uploaded required slot no longer
    // trips the "required" error. Only a slot with no photo at all (never uploaded and
    // nothing picked now) is flagged.
    const missing = slots.filter((s) => s.required && !photos[s.key]);
    if (missing.length) {
      setInvalidSlots(Object.fromEntries(missing.map((s) => [s.key, true])));
      notify(`${missing.map((s) => s.label).join(", ")} required`, "bad");
      return;
    }

    const fd = new FormData();
    fd.append("consignmentNo", verified.cnNo);
    // A freshly picked file always wins. Otherwise, if this slot already has an
    // uploaded photo (existing: true) we re-send its blob as the file — that's what
    // lets an untouched required slot pass a backend that expects a file on every
    // POST, without making the user reselect a photo they already uploaded. A slot
    // with neither (nothing existing, nothing picked) sends nothing, same as before.
    slots.forEach((s) => {
      const p = photos[s.key];
      if (!p) return;
      const file = p.file || (p.existing && p.blob ? new File([p.blob], `${s.key}.jpg`, { type: p.blob.type || "image/jpeg" }) : null);
      if (file) fd.append(s.key, file);
    });
    fd.append("uploadedAt", new Date().toISOString());

    setUploading(true);
    setProgress(0);
    try {
      let response;
      if (demo) {
        for (let p = 0; p <= 100; p += 10) {
          setProgress(p);
          await sleep(80);
        }
      } else {
        response = await uploadWithProgress(fd);
      }
      notify(`POD uploaded for ${verified.cnNo}`, "ok");
      onUploaded?.(verified.cnNo, response);
      Object.values(photos).forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos({});
      setCnNo("");
      setVerified(null);
    } catch (err) {
      notify(err.response?.data?.message || err.message || "Upload failed", "bad");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 400);
    }
  };

  return (
    <div className="pod-root">
      <header className="pod-appbar">
        <button type="button" aria-label="Back" onClick={onBack}>
          <BackIcon />
        </button>
        <h1>POD Upload</h1>
      </header>

      <main className="pod-main">
        {/* Consignment */}
        <section className="pod-card">
          <form className="pod-cn-row" onSubmit={handleVerify} autoComplete="off">
            <input
              className={`pod-input ${cnInvalid ? "invalid" : ""}`}
              type="text"
              value={cnNo}
              onChange={handleCnChange}
              placeholder="Consignment No."
              aria-label="Consignment number"
              autoCapitalize="characters"
            />
            <button className="pod-btn pod-btn-primary" type="submit" disabled={verifying}>
              {verifying ? <span className="pod-spinner light" /> : "Verify"}
            </button>
          </form>

          {verified && (
            <div className="pod-cn-info">
              <div className="row"><span>Consignor</span><b>{verified.consignor || "-"}</b></div>
              <div className="row"><span>Consignee</span><b>{verified.consignee || "-"}</b></div>
              <div className="row"><span>Status</span><b>{verified.status || "-"}</b></div>
            </div>
          )}
        </section>

        {/* Photos */}
        <section className="pod-card">
          <div className="pod-section-label">
            <span>Proof of Delivery</span>
            <span>{Object.keys(photos).length} / {slots.length}</span>
          </div>
          <div className="pod-photos">
            {slots.map((s) => (
              <PhotoSlot
                key={s.key}
                slot={s}
                photo={photos[s.key]}
                invalid={invalidSlots[s.key]}
                removing={removingSlots[s.key]}
                onCamera={openCamera}
                onPick={setPhoto}
                onRemove={removePhoto}
              />
            ))}
          </div>
          {progress !== null && (
            <div className="pod-progress">
              <i style={{ width: `${progress}%` }} />
            </div>
          )}
        </section>
      </main>

      {createPortal(
        <div className="pod-portal">
          <footer className="pod-footer">
            <div className="pod-footer-inner">
              <button className="pod-btn pod-btn-submit" type="button" onClick={handleSubmit} disabled={uploading}>
                {uploading ? (<><span className="pod-spinner" /> Uploading…</>) : "Submit"}
              </button>
            </div>
          </footer>
        </div>,
        document.body
      )}

      {createPortal(
        <div className="pod-portal">
          <div className={`pod-toast ${toast.show ? "show" : ""} ${toast.type}`}>{toast.msg}</div>
        </div>,
        document.body
      )}

      {camera &&
        createPortal(
          <div className="pod-portal">
            <CameraModal
              title={camera.slot.label}
              onClose={closeCamera}
              onCapture={cameraCapture}
              onFallback={cameraFallback}
              notify={notify}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
